import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { 
  LayoutDashboard, BookOpen, Users, Database, GraduationCap,
  User, Settings, LogOut, Menu, X, FileText, ClipboardList, HelpCircle, MessageSquare, Rocket, Sparkles, ChevronDown, Mail, UserPlus, MapPin
} from "lucide-react";
import { useState, useEffect, useCallback } from "react";

const publicNavItems = [
  { path: "/signup", label: "Sign Up", icon: UserPlus },
  { path: "/apply", label: "Enroll Now", icon: GraduationCap },
  { path: "/skills-matrix", label: "Search Talent", icon: Database },
  { path: "/programs", label: "Browse Programs", icon: BookOpen },
  { path: "/workforce-lookup", label: "Workforce Boards", icon: MapPin },
  { path: "/faq", label: "FAQ", icon: HelpCircle },
  { path: "/contact", label: "Contact", icon: MessageSquare },
];

const NAV_GROUPS = [
  {
    label: "Learning",
    icon: BookOpen,
    items: [
      { path: "/programs", label: "Programs", icon: BookOpen },
      { path: "/strengths", label: "My Strengths", icon: Sparkles },
      { path: "/profile", label: "My Profile", icon: User },
    ],
  },
  {
    label: "Community",
    icon: Users,
    items: [
      { path: "/community", label: "Community Feed", icon: MessageSquare },
      { path: "/mentorship", label: "Mentorship", icon: Users },
      { path: "/alumni-directory", label: "Alumni Directory", icon: GraduationCap },
      { path: "/stories", label: "Success Stories", icon: Sparkles },
    ],
  },
  {
    label: "Resources",
    icon: HelpCircle,
    items: [
      { path: "/talent", label: "Talent Database", icon: Database },
      { path: "/faq", label: "FAQ", icon: HelpCircle },
      { path: "/contact", label: "Contact", icon: MessageSquare },
      { path: "/workforce-lookup", label: "Workforce Boards", icon: Database },
    ],
  },
];

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/messages", label: "Messages", icon: Mail },
  { path: "/strengths", label: "Strengths", icon: Sparkles },
  { path: "/community", label: "Community", icon: MessageSquare },
  { path: "/programs", label: "Programs", icon: BookOpen },
  { path: "/mentorship", label: "Mentorship", icon: Users },
  { path: "/talent", label: "Talent DB", icon: Database },
  { path: "/alumni-directory", label: "Alumni", icon: GraduationCap },
  { path: "/faq", label: "FAQ", icon: HelpCircle },
  { path: "/contact", label: "Contact", icon: MessageSquare },
];

const LOGO_LIGHT_BG = "https://customer-assets.emergentagent.com/job_aspiritech-portal/artifacts/xoa2g8yb_Tara-May-39CC5659_launch_no4-black-low-res.png";
const LOGO_DARK_BG = "https://customer-assets.emergentagent.com/job_aspiritech-portal/artifacts/ds0opo4r_Tara-May-39CC5659_launch_no4-white-low-res.png";

// CSS filter that tints the white (dark-bg) logo to brand gold #FFD507
// Computed via standard CSS filter colour-shift technique.
const GOLD_FILTER = "brightness(0) saturate(100%) invert(86%) sepia(58%) saturate(2917%) hue-rotate(354deg) brightness(102%) contrast(108%)";

export const Logo = ({ variant = "light", height = 40 }) => {
  const src = variant === "gold" ? LOGO_DARK_BG : (variant === "dark" ? LOGO_DARK_BG : LOGO_LIGHT_BG);
  return (
    <img
      src={src}
      alt="Aspiritech Academy"
      style={{
        height: `${height}px`,
        width: "auto",
        filter: variant === "gold" ? GOLD_FILTER : undefined,
      }}
      className="object-contain"
    />
  );
};

// Clean text wordmark — used in nav where the script logo is too thin to read.
export const Wordmark = ({ tone = "dark", size = "md" }) => {
  // tone: 'dark' (navy on light bg) | 'light' (white on dark bg)
  const sizeCls = size === "lg"
    ? "text-2xl md:text-3xl"
    : size === "sm"
    ? "text-base"
    : "text-lg md:text-xl";
  const colorCls = tone === "light" ? "text-white" : "text-[#1E3A8A]";
  return (
    <span className={`inline-flex items-baseline gap-1.5 font-heading font-semibold tracking-tight ${sizeCls} ${colorCls}`}>
      <span>Aspiritech Academy</span>
      <span className="font-semibold opacity-90">Talent Portal</span>
    </span>
  );
};

export const Header = ({ variant = "default" }) => {
  const { user, logout, login } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  const fetchUnread = useCallback(async () => {
    if (!user) return;
    try {
      const r = await fetch(`${API}/messages/unread-count`, { credentials: "include" });
      if (!r.ok) return;
      const d = await r.json();
      setUnread(d.unread || 0);
    } catch (_) { /* ignore */ }
  }, [user]);

  useEffect(() => {
    if (!user) { setUnread(0); return; }
    fetchUnread();
    const onRefresh = () => fetchUnread();
    window.addEventListener("aspiritech:messages:refresh-unread", onRefresh);
    const id = setInterval(fetchUnread, 60000);
    return () => {
      window.removeEventListener("aspiritech:messages:refresh-unread", onRefresh);
      clearInterval(id);
    };
  }, [user, fetchUnread]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="glass-header" data-testid="main-header">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="logo-link">
          <Wordmark tone="dark" size="md" />
        </Link>

        {user ? (
          <nav className="hidden md:flex items-center gap-2 lg:gap-4">
            <Link
              to="/dashboard"
              className={`flex items-center gap-2 text-sm font-medium transition-colors px-2 ${
                location.pathname === "/dashboard" ? "nav-link-active" : "nav-link"
              }`}
              data-testid="nav-dashboard"
            >
              <LayoutDashboard className="w-4 h-4" /> Dashboard
            </Link>

            {NAV_GROUPS.map((group) => {
              const GroupIcon = group.icon;
              const isActive = group.items.some((i) => location.pathname === i.path);
              return (
                <DropdownMenu key={group.label}>
                  <DropdownMenuTrigger
                    className={`flex items-center gap-1.5 text-sm font-medium transition-colors px-2 outline-none focus:outline-none ${
                      isActive ? "nav-link-active" : "nav-link"
                    }`}
                    data-testid={`nav-group-${group.label.toLowerCase()}`}
                  >
                    <GroupIcon className="w-4 h-4" /> {group.label}
                    <ChevronDown className="w-3.5 h-3.5 opacity-70" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56 bg-white border-slate-200 shadow-lg rounded-xl">
                    {group.items.map((item) => {
                      const Icon = item.icon;
                      return (
                        <DropdownMenuItem
                          key={item.path}
                          onClick={() => navigate(item.path)}
                          className="text-slate-700 hover:text-slate-900 hover:bg-slate-50 cursor-pointer"
                          data-testid={`nav-item-${item.label.toLowerCase().replace(/ /g, "-")}`}
                        >
                          <Icon className="w-4 h-4 mr-2 text-slate-500" />
                          <span>{item.label}</span>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })}

            {user.role === 'admin' && (
              <Link
                to="/admin"
                className={`flex items-center gap-2 text-sm font-medium transition-colors px-2 ${
                  location.pathname === '/admin' ? "nav-link-active" : "nav-link"
                }`}
                data-testid="nav-admin"
              >
                <Settings className="w-4 h-4" /> Admin
              </Link>
            )}
            {(user.role === 'instructor' || user.role === 'admin') && (
              <Link
                to="/instructor"
                className={`flex items-center gap-2 text-sm font-medium transition-colors px-2 ${
                  location.pathname.startsWith('/instructor') ? "nav-link-active" : "nav-link"
                }`}
                data-testid="nav-instructor"
              >
                <ClipboardList className="w-4 h-4" /> My Classes
              </Link>
            )}
          </nav>
        ) : (
          <nav className="hidden md:flex items-center gap-6">
            {publicNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                    isActive ? "nav-link-active" : "nav-link"
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        )}

        <div className="flex items-center gap-4">
          {user ? (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger className="focus:outline-none" data-testid="user-menu-trigger">
                  <Avatar className="h-9 w-9 border-2 border-brand-gold/30 hover:border-brand-gold transition-colors">
                    <AvatarImage src={user.picture} alt={user.name} />
                    <AvatarFallback className="bg-brand-navy text-white text-sm">
                      {user.name?.charAt(0)?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-white border-slate-200 shadow-lg rounded-xl">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-slate-900">{user.name}</p>
                    <p className="text-xs text-slate-500">{user.email}</p>
                    <span className="inline-block mt-1 badge-gold capitalize">{user.role}</span>
                  </div>
                  <DropdownMenuSeparator className="bg-slate-100" />
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="text-slate-600 hover:text-slate-900 hover:bg-slate-50 cursor-pointer" data-testid="menu-profile">
                    <User className="w-4 h-4 mr-2" /> My Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/working-style')} className="text-slate-600 hover:text-slate-900 hover:bg-slate-50 cursor-pointer" data-testid="menu-working-style">
                    <Rocket className="w-4 h-4 mr-2" /> Working With Me
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/apply')} className="text-slate-600 hover:text-slate-900 hover:bg-slate-50 cursor-pointer" data-testid="menu-apply">
                    <FileText className="w-4 h-4 mr-2" /> Academy Enrollment
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/documents')} className="text-slate-600 hover:text-slate-900 hover:bg-slate-50 cursor-pointer" data-testid="menu-documents">
                    <FileText className="w-4 h-4 mr-2" /> Upload Documents
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-slate-100" />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-500 hover:text-red-600 hover:bg-red-50 cursor-pointer" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" /> Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <button className="md:hidden p-2 text-slate-500 hover:text-brand-navy" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </>
          ) : (
            <div className="flex items-center gap-3">
              <button className="md:hidden p-2 text-slate-500 hover:text-brand-navy" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn-public">
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              <Link to="/login" className="btn-primary text-sm inline-flex items-center" data-testid="header-login-btn">
                Sign In
              </Link>
            </div>
          )}
        </div>
      </div>

      {mobileMenuOpen && (
        <nav className="md:hidden pt-4 mt-3 border-t border-slate-200/60 max-w-7xl mx-auto">
          {(user ? navItems : publicNavItems).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-3 rounded-lg ${
                  isActive ? "text-brand-navy bg-brand-navy/5 font-semibold" : "text-slate-500"
                }`}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <Icon className="w-5 h-5" /> {item.label}
              </Link>
            );
          })}
          {user && (user.role === 'instructor' || user.role === 'admin') && (
            <Link to="/instructor" onClick={() => setMobileMenuOpen(false)}
              className={`flex items-center gap-3 px-3 py-3 rounded-lg ${
                location.pathname.startsWith('/instructor') ? "text-brand-navy bg-brand-navy/5 font-semibold" : "text-slate-500"
              }`}
              data-testid="mobile-nav-my-classes"
            >
              <ClipboardList className="w-5 h-5" /> My Classes
            </Link>
          )}
          {user?.role === 'admin' && (
            <Link to="/admin" onClick={() => setMobileMenuOpen(false)}
              className={`flex items-center gap-3 px-3 py-3 rounded-lg ${
                location.pathname === '/admin' ? "text-brand-navy bg-brand-navy/5 font-semibold" : "text-slate-500"
              }`}
              data-testid="mobile-nav-admin"
            >
              <Settings className="w-5 h-5" /> Admin
            </Link>
          )}
        </nav>
      )}
    </header>
  );
};

export const Footer = () => (
  <footer className="bg-[#0F1B3D] text-white/70 mt-16" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid gap-8 md:grid-cols-3 items-start">
      <div>
        <Link to="/" className="inline-flex items-center" data-testid="footer-logo">
          <Logo variant="dark" height={144} />
        </Link>
        <p className="text-sm text-white/60 mt-4 max-w-xs">
          A launchpad for autistic and neurodivergent adults pursuing tech careers.
        </p>
      </div>
      <div className="text-sm">
        <p className="text-white font-semibold uppercase tracking-wider text-xs mb-3">Programs</p>
        <ul className="space-y-2">
          <li><Link to="/programs" className="hover:text-[#FFD507] transition">Browse Programs</Link></li>
          <li><Link to="/apply" className="hover:text-[#FFD507] transition">Enroll in the Academy</Link></li>
          <li><Link to="/skills-matrix" className="hover:text-[#FFD507] transition">Talent Database</Link></li>
          <li><Link to="/workforce-lookup" className="hover:text-[#FFD507] transition">Find your Workforce Board</Link></li>
        </ul>
      </div>
      <div className="text-sm">
        <p className="text-white font-semibold uppercase tracking-wider text-xs mb-3">Connect</p>
        <ul className="space-y-2">
          <li><Link to="/contact" className="hover:text-[#FFD507] transition">Contact Us</Link></li>
          <li><Link to="/mentor-signup" className="hover:text-[#FFD507] transition" data-testid="footer-become-mentor">Become A Mentor</Link></li>
          <li><Link to="/faq" className="hover:text-[#FFD507] transition">FAQ</Link></li>
          <li><Link to="/grievance-policy" className="hover:text-[#FFD507] transition" data-testid="footer-grievance-link">Grievance Procedure</Link></li>
          <li><a href="mailto:academy@aspiritech.org" className="hover:text-[#FFD507] transition">academy@aspiritech.org</a></li>
        </ul>
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-white/40">
        <p>© {new Date().getFullYear()} Aspiritech Academy. Training and career launchpad for neurodivergent adults.</p>
        <p>Built for accessibility · Light mode by design</p>
      </div>
    </div>
  </footer>
);

export const PageLayout = ({ children, title, subtitle }) => {
  return (
    <div className="min-h-screen flex flex-col bg-surface-app">
      <Header />
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
        {(title || subtitle) && (
          <div className="mb-8">
            {title && <h1 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-2">{title}</h1>}
            {subtitle && <p className="text-slate-500">{subtitle}</p>}
          </div>
        )}
        {children}
      </main>
      <Footer />
    </div>
  );
};

export const LightPageLayout = ({ children, title, subtitle }) => {
  return (
    <div className="min-h-screen flex flex-col bg-surface-app">
      <Header />
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
        {(title || subtitle) && (
          <div className="mb-8">
            {title && <h1 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-2">{title}</h1>}
            {subtitle && <p className="text-slate-500">{subtitle}</p>}
          </div>
        )}
        {children}
      </main>
      <Footer />
    </div>
  );
};
